// The picture on the account button in the header. An agent's is their photo from Settings
// (their upload, else the board's member photo, agent.settings.photo); everyone else keeps the
// sign-in picture or their initials. It is asked for once per page load and kept for the tab,
// so a reload shows it at once; the settings page updates it when the photo changes.
import { useEffect, useState } from 'react';
import { getPhoto } from '../services/agentSettingsService';

const STORAGE_KEY = 'keyta-account-photo';
const listeners = new Set();
const asked = new Set(); // user ids asked about in this page load

const readStored = () => {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return value && typeof value === 'object' ? value : null;
  } catch (_) {
    return null;
  }
};

let current = readStored(); // { userId, url }

/** The photo known for `userId`: a URL, null for none, or undefined when not known yet. */
export const accountPhotoFor = (userId) =>
  current && userId != null && current.userId === String(userId) ? current.url : undefined;

/** Set `userId`'s photo (null for none) and tell the header. */
export const setAccountPhoto = (userId, url) => {
  if (userId == null) return;
  current = { userId: String(userId), url: url || null };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch (_) {
    /* no storage: the photo is asked for again on the next load */
  }
  listeners.forEach((listener) => listener());
};

/**
 * The account picture of `user` when they are an agent (pass null for anyone else): the
 * photo known so far, asked for once per page load. Null when there is none or it failed.
 */
export const useAccountPhoto = (user) => {
  const userId = user?.id ?? null;
  const [, setVersion] = useState(0);

  useEffect(() => {
    const listener = () => setVersion((version) => version + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  useEffect(() => {
    if (userId == null || asked.has(String(userId))) return;
    asked.add(String(userId));
    // A picture never breaks the header: any failure leaves the initials or sign-in picture.
    Promise.resolve()
      .then(getPhoto)
      .then((photo) => setAccountPhoto(userId, photo?.url || null))
      .catch((err) => console.warn('Loading the account photo failed:', err?.message || err));
  }, [userId]);

  return userId == null ? null : accountPhotoFor(userId) || null;
};
